/**
 * The tech day view, the default landing page (sections 11.0 and 11.1; ADR
 * 0032): the stat row, the ribbon timeline and the status columns, the full
 * width of the page. A block opens in a popup over them (ADR 0184). An owner
 * can switch to anyone's day (11.2).
 *
 * Closed is done, not logged (v0.9.0; ADR 0185): the board's columns are
 * Needs you · Ready to log · Logged - Open · Closed, a logged card sits in
 * the last two by its ticket's state, and the Open tickets tile counts the
 * tech's open tickets from every day. There, a ticket's blocks share one card
 * (ADR 0189), so a column counts tickets.
 *
 * A card has a right-click menu, and several cards can be selected for what
 * applies to them all, from a bar at the foot (ADR 0194, decisions 3 and 5;
 * `boardActions`).
 *
 * The day refreshes every 30 seconds while the tab is visible (11.3), and
 * each refresh also checks for a new release (ADR 0117).
 */
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import type { Day, TicketAction } from "../api/types";
import { useSession } from "../app/session-context";
import { useSelectedDay } from "../app/useSelectedDay";
import { useViewedUserId } from "../app/useViewedUser";
import { checkForNewVersion } from "../app/version";
import { Panel } from "../components/ui/Panel";
import { HeroTile, ProgressBar, StatTile } from "../components/ui/StatTile";
import { StatusColumn } from "../components/ui/StatusColumn";
import { formatShortDay, todayIn } from "../lib/dates";
import { formatDuration, formatHours } from "../lib/durations";
import { ActiveCallPopup } from "./day/ActiveCallPopup";
import { RingingCalls } from "./day/RingingCalls";
import { AnnounceContext } from "./day/announce";
import { BlockCard } from "./day/BlockCard";
import type { OpenOptions, PopupStart } from "./day/BlockDetails";
import { type AdoptLiveParts, BlockPopup } from "./day/BlockPopup";
import { boardCards, cardColumn, ticketCardOf } from "./day/board";
import { useBoardActions } from "./day/boardActions";
import { BOARD_KEYS_HINT } from "./day/boardKeys";
import { DayFactsContext } from "./day/dayContext";
import { LoggedEntryCard } from "./day/LoggedEntryCard";
import type { LogStart } from "./day/LogToAutotask";
import { OpenTicketsDrawer } from "./day/OpenTicketsTray";
import {
  type BlockView,
  COLUMNS,
  type ColumnId,
  type LoggedEntryView,
  blockViews,
  startsOwnFocus,
  loggedEntryViews,
  newlyClosed,
  nextCard,
  ribbonActivities,
  ticketOf,
} from "./day/model";
import { RibbonTimeline } from "./day/RibbonTimeline";
import { ACTION_POLL_MS, isUnfinished, ticketActionQuery } from "./day/ticketActions";
import { TicketCard } from "./day/TicketCard";
import { ageOf, ticketName } from "./day/tickets";
import { oldestOf, ownTickets, useTrayTickets } from "./day/trayTickets";
import { useDragToMerge } from "./day/useDragToMerge";

export const POLL_MS = 30_000;

/** Retry what may pass on a second try; a refusal (4xx) won't. */
function retry(failures: number, error: Error): boolean {
  return !(error instanceof ApiError && error.status < 500) && failures < 2;
}

function errorMessage(error: Error): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You can only see your own day.";
    }
    if (error.status === 404) {
      return "There's no user with that ID.";
    }
    return error.detail ?? error.message;
  }
  return error.message;
}

/** The current time, refreshed every minute: the "now" marker and "today". */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return now;
}

function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** "To review" links to the oldest day in 14 with blocks still to log (11.1). */
function OldestLink({ oldest, current }: { oldest: string | null; current: string }) {
  const [searchParams] = useSearchParams();
  if (oldest === null || oldest === current) {
    return null;
  }
  const params = new URLSearchParams(searchParams);
  params.set("date", oldest);
  return (
    <Link
      to={{ search: `?${params.toString()}` }}
      className="text-[12.5px] font-semibold whitespace-nowrap text-brand-ink hover:underline"
    >
      {`Oldest: ${formatShortDay(oldest)}`}
    </Link>
  );
}

/** Every block of the tech's still to log, on any day (#873). */
function toLog(ages: Day["unlogged_by_age"]): number {
  return ages.today + ages.days_1_to_3 + ages.days_4_to_7 + ages.older;
}

/**
 * Phase 2's unsubmitted aging indicator (section 19): every block of the
 * tech's still to log, by age from today, so nothing is forgotten.
 */
function Aging({ ages }: { ages: Day["unlogged_by_age"] }) {
  const parts = [
    ages.today > 0 ? `${String(ages.today)} today` : null,
    ages.days_1_to_3 > 0 ? `${String(ages.days_1_to_3)} from 1–3 days ago` : null,
    ages.days_4_to_7 > 0 ? `${String(ages.days_4_to_7)} from 4–7 days ago` : null,
    ages.older > 0 ? `${String(ages.older)} older` : null,
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) {
    return null;
  }
  return (
    <ul aria-label="Waiting to be logged" className="flex flex-wrap gap-x-2 text-[12.5px] text-muted">
      {parts.map((part, index) => (
        <li key={part} className={index > 0 ? "before:mr-2 before:content-['·']" : undefined}>
          {part}
        </li>
      ))}
    </ul>
  );
}

/**
 * The Open tickets tile (v0.9.0's P2b; the roadmap: "a tile 'Open tickets 3 ·
 * oldest 6 d'"): the tech's tickets not closed, from every day, as the tray's
 * API gives them (ADR 0183), whatever day is shown. Not those they're only a
 * secondary resource on (ADR 0192): they aren't theirs.
 *
 * "Show them" opens the open-tickets tray (P5; ADR 0188), while any are open.
 */
function OpenTicketsTile({
  userId,
  timeZone,
  now,
  onShow,
}: {
  /** Whose tickets: the viewed tech's; null for the signed-in user's own. */
  userId: string | null;
  timeZone: string;
  now: Date;
  /** Opens the tray (P5). */
  onShow?: () => void;
}) {
  const tray = useTrayTickets(userId);
  const tickets = ownTickets(tray.data?.tickets ?? []);
  const oldest = oldestOf(tickets);
  let note: string | undefined;
  if (tray.data === undefined) {
    note = tray.isError ? "couldn't load" : undefined;
  } else if (tickets.length === 0) {
    note = "all closed";
  } else if (oldest !== null) {
    note = `oldest ${ageOf(oldest, now, timeZone)}`;
  }
  return (
    <StatTile
      label="Open tickets"
      value={tray.data === undefined ? "—" : String(tickets.length)}
      note={note}
      aside={
        onShow !== undefined && tickets.length > 0 ? (
          <button
            type="button"
            onClick={onShow}
            className="cursor-pointer text-[12.5px] font-semibold whitespace-nowrap text-brand-ink hover:underline"
          >
            Show them
          </button>
        ) : null
      }
    >
      {tray.data === undefined ? null : (
        <p className="text-[12.5px] text-muted">{tickets.length > 0 ? "From every day" : "Nothing left open"}</p>
      )}
    </StatTile>
  );
}

function StatRow({
  day,
  isToday,
  userId,
  now,
  onShowTickets,
}: {
  day: Day;
  isToday: boolean;
  userId: string | null;
  now: Date;
  /** Opens the open-tickets tray (P5). */
  onShowTickets: () => void;
}) {
  const { stats } = day;
  const capacitySeconds = day.user.daily_capacity_hours * 3600;
  const ofCapacity = capacitySeconds > 0 ? Math.round((stats.captured_seconds / capacitySeconds) * 100) : 0;
  const loggedBlocks = day.blocks.filter((b) => b.status === "submitted").length;
  const inAutotask = day.logged_in_autotask.length;
  const loggedNote = count(loggedBlocks, "block", "blocks") + (inAutotask > 0 ? ` · ${String(inAutotask)} in Autotask` : "");
  const loggedShare = stats.captured_seconds > 0 ? (stats.logged_seconds / stats.captured_seconds) * 100 : 0;
  const owed = toLog(day.unlogged_by_age);

  // From 1100 px the tiles share a row, at least 158 px tall: it grows when a
  // tile needs more room (the To review tile's lines, with five tiles in a
  // narrow window), as the open-tickets mockup's does, rather than spilling.
  return (
    <div className="flex flex-col gap-3 min-[1100px]:min-h-[158px] min-[1100px]:flex-row">
      <HeroTile
        label={isToday ? "Captured today" : "Captured"}
        value={formatDuration(stats.captured_seconds)}
        note={`${String(ofCapacity)}% of ${formatHours(day.user.daily_capacity_hours)}`}
      />
      {/* Every block still to log, on any day, then by age (#873): the
          number was this day's, and the line under it every day's. On
          another day, how many of them are on it. */}
      <StatTile
        label="To review"
        value={String(owed)}
        note={owed === 1 ? "block to log" : "blocks to log"}
        aside={<OldestLink oldest={day.oldest_unlogged_day} current={day.day} />}
      >
        <Aging ages={day.unlogged_by_age} />
        {!isToday && owed > 0 ? (
          <p className="text-[12.5px] text-muted">
            {stats.to_review === 0 ? "None on this day" : `${String(stats.to_review)} of them on this day`}
          </p>
        ) : null}
      </StatTile>
      <StatTile
        label="Logged"
        value={formatDuration(stats.logged_seconds)}
        note={loggedNote}
      />
      <StatTile label="Capture gap" value={formatDuration(stats.gap_seconds)} note="not yet logged">
        <ProgressBar percent={loggedShare} label="Captured time logged" />
      </StatTile>
      <OpenTicketsTile userId={userId} timeZone={day.user.timezone} now={now} onShow={onShowTickets} />
    </div>
  );
}

/**
 * The closes under way on this day (ADR 0182): each followed until it ends,
 * and then the day, and the Open tickets tile, read again. So a card moves to
 * Closed as soon as its ticket closes, with its popup open or not.
 */
function useClosesUnderWay(blocks: readonly BlockView[], entries: readonly LoggedEntryView[]): void {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const ids = [...blocks.map((b) => b.ticket), ...entries.map((e) => e.ticket)]
    .filter((t) => t !== null && t.state === "closing" && t.closeActionId !== null)
    .map((t) => t?.closeActionId ?? "");
  const ended = useQueries({
    queries: [...new Set(ids)].map((id) => ({
      queryKey: ticketActionQuery(id),
      queryFn: () => api.getTicketAction(id),
      refetchInterval: (query: { state: { data: TicketAction | undefined } }) =>
        isUnfinished(query.state.data) ? ACTION_POLL_MS : false,
      retry,
    })),
    combine: (results) =>
      results
        .map((r) => r.data)
        .filter((a): a is TicketAction => a !== undefined && !isUnfinished(a))
        .map((a) => `${a.id}:${a.state}`)
        .join(" "),
  });
  useEffect(() => {
    if (ended !== "") {
      void queryClient.invalidateQueries({ queryKey: ["day"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    }
  }, [ended, queryClient]);
}

function DayView({
  day,
  userId,
  refreshFailed,
  liveHost,
  adoptLiveParts,
}: {
  day: Day;
  /** The viewed tech, for an owner viewing another's day; null for the signed-in user's own. */
  userId: string | null;
  refreshFailed: boolean;
  /** Where the status region goes: never hidden by the block popup (newLiveHost). */
  liveHost: HTMLElement;
  /** Moves the live parts into the block popup while it's open, and back (ADR 0184). */
  adoptLiveParts: AdoptLiveParts;
}) {
  const now = useNow();
  const all = useMemo(() => blockViews(day), [day]);
  // The block whose popup is open (ADR 0184). `?block=<id>` opens the day
  // with it open, as Health's links to a failed submission do (Idea #289). An
  // ID not on this day is ignored; a dismissed block's opens once "Show
  // dismissed" shows it, as its panel did.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(() => {
    const requested = searchParams.get("block");
    return all.some((b) => b.id === requested) ? requested : null;
  });
  // Where the open popup's Log form starts: the ticket its block was dropped
  // on (v0.9.0's P4), until the popup closes.
  const [logStart, setLogStart] = useState<LogStart | null>(null);
  // Where the open popup starts, for its block (ADR 0194): on a pane, or with
  // the Log form's Ticket list open. `serial` tells each request apart.
  const [popupStart, setPopupStart] = useState<(PopupStart & { serial: number }) | null>(null);
  const startSerial = useRef(0);
  // The open-tickets tray (v0.9.0's P5): open while the address says
  // `tickets=open`, as the reminder email's "Close them in Axon Time" links
  // (ADR 0185), so it stays open from day to day. What opened it takes focus
  // back when it closes.
  const trayOpen = searchParams.get("tickets") === "open";
  const trayOpener = useRef<Element | null>(null);
  // Opened here (not open already as the day loaded): focus goes into it.
  const [trayFocus, setTrayFocus] = useState(false);
  const openTray = () => {
    trayOpener.current = document.activeElement;
    setTrayFocus(true);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("tickets", "open");
        return params;
      },
      { replace: true },
    );
  };
  const closeTray = () => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete("tickets");
        return params;
      },
      { replace: true },
    );
    const back = trayOpener.current;
    trayOpener.current = null;
    if (back instanceof HTMLElement && back.isConnected && back !== document.body) {
      back.focus();
    }
  };
  const timeZone = day.user.timezone;
  const isToday = day.day === todayIn(timeZone, now);
  // Dismissed blocks are hidden unless "Show dismissed" is on (section 11.1),
  // and then shown, faded, in the column they left.
  const [showDismissed, setShowDismissed] = useState(false);
  const dismissedCount = all.filter((b) => b.kind === "dismissed").length;
  const blocks = useMemo(
    () =>
      all
        .map((b) => (b.column === null && showDismissed ? { ...b, column: b.dismissedFrom } : b))
        .filter((b) => b.column !== null),
    [all, showDismissed],
  );
  // What Previous and Next step through: the board's blocks in time order.
  const order = useMemo(
    () => [...blocks].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt) || a.id.localeCompare(b.id)),
    [blocks],
  );
  // The ribbon draws what the board shows (ADR 0094): a hidden dismissed
  // block's activities leave with it, unless it was dismissed as already
  // logged; time logged in Autotask is never drawn there.
  const ribbon = useMemo(() => ribbonActivities(day, showDismissed), [day, showDismissed]);
  const entries = useMemo(() => loggedEntryViews(day), [day]);
  // Section 11.0's logged moment, now the closed moment (v0.9.0's P2b): a
  // card that lands in Closed while the page is open flashes the logged
  // green, which now means closed. One logged and left open lands in Logged -
  // Open without it.
  const seen = useRef(new Map<string, ColumnId | null>());
  const [justClosed, setJustClosed] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const placed = [...blocks, ...entries];
    const closed = newlyClosed(seen.current, placed);
    if (closed.size > 0) {
      setJustClosed(closed);
    }
    seen.current = new Map(placed.map((c) => [c.id, c.column]));
  }, [blocks, entries]);
  useClosesUnderWay(blocks, entries);
  // The board's cards: in Logged - Open and Closed, one per ticket (ADR 0189).
  const onBoard = useMemo(() => boardCards(blocks, entries), [blocks, entries]);
  const open = blocks.find((b) => b.id === openId) ?? null;
  const selectedId = open?.id ?? null;
  // The open block's ticket's card, when it shares one: its popup lists the
  // ticket's other time entries that day.
  const openTicketCard = open === null ? null : ticketCardOf(onBoard, open.id);

  // What the tech's last action did, for the status region (#247). Keyed, so
  // the same words twice are said twice.
  const [said, setSaid] = useState({ message: "", key: 0 });
  const announce = useCallback((message: string) => {
    setSaid((last) => ({ message, key: last.key + 1 }));
  }, []);

  // Each block's card's button, to give focus to (#247): a ticket's card is
  // every one of its blocks' (ADR 0189).
  const cards = useRef(new Map<string, HTMLButtonElement>());
  const showDismissedBox = useRef<HTMLInputElement>(null);
  // A card with focus that moves to another column is a new button there:
  // focus goes with it, as when a block closed after logging lands in Logged.
  // So does one that joins its ticket's card, or becomes it.
  const refocus = useRef<string | null>(null);
  const cardRef = (ids: readonly string[]) => (button: HTMLButtonElement | null) => {
    if (button === null) {
      for (const id of ids) {
        if (document.activeElement !== null && document.activeElement === cards.current.get(id)) {
          refocus.current = id;
        }
        cards.current.delete(id);
      }
    } else {
      for (const id of ids) {
        cards.current.set(id, button);
      }
      if (refocus.current !== null && ids.includes(refocus.current) && document.activeElement !== button) {
        button.focus();
      }
    }
  };
  useLayoutEffect(() => {
    // After the cards are in place: a card that went for good takes nothing.
    refocus.current = null;
  });

  // What opened the popup, a card or a capsule, and for which block: focus
  // goes back to it when the popup closes on that block (ADR 0184).
  const opener = useRef<{ element: Element | null; blockId: string } | null>(null);
  // The block the popup showed as it closed.
  const closedOn = useRef<string | null>(null);
  /**
   * Shows a block's popup, open already or not: on a pane (`pane`, with the
   * Dismiss pane's `reason`), with the Log form's Ticket list open
   * (`ticketListOpen`), its form's first field focused (`focusForm`), Close
   * ticket asking (`closeTicket`), or with its Log form on a ticket
   * (`logStart`, as a drop). With none, as a click on its card opens it.
   */
  const showBlock = (id: string, options: OpenOptions = {}) => {
    if (startsOwnFocus(options)) {
      startSerial.current += 1;
      setPopupStart({
        blockId: id,
        pane: options.pane,
        reason: options.reason,
        ticketListOpen: options.ticketListOpen,
        focusForm: options.focusForm,
        closeTicket: options.closeTicket,
        serial: startSerial.current,
      });
    } else {
      setPopupStart(null);
    }
    if (options.logStart !== undefined) {
      setLogStart(options.logStart);
    }
    setOpenId(id);
  };
  /** Opens a block's popup (showBlock), focus going back to what opened it once it closes. */
  const openBlock = (id: string, options: OpenOptions = {}) => {
    opener.current = { element: document.activeElement, blockId: id };
    showBlock(id, options);
  };
  const closePopup = () => {
    closedOn.current = openId;
    setOpenId(null);
    setLogStart(null);
    setPopupStart(null);
    // A link's block, once closed, doesn't open again on a reload.
    if (searchParams.has("block")) {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          params.delete("block");
          return params;
        },
        { replace: true },
      );
    }
  };
  // Once the popup's gone: focus back on what opened it, or else on the card
  // of the block it showed last. Not when that block left the board: the card
  // that takes its place has focus already (below).
  const focusAfterClosing = () => {
    const id = closedOn.current;
    const card = id === null ? undefined : cards.current.get(id);
    if (card === undefined) {
      return;
    }
    const from = opener.current?.blockId === id ? opener.current.element : null;
    if (from instanceof HTMLElement && from.isConnected && from !== document.body) {
      from.focus();
    } else {
      card.focus();
    }
  };
  // When the open block leaves the board (dismissed, or merged into another),
  // its popup closes, and focus falls to the page: the card that takes its
  // place gets focus instead (#247). Never when focus is somewhere else.
  const board = useRef(blocks);
  useEffect(() => {
    const before = board.current;
    board.current = blocks;
    const gone = before.find((b) => b.id === openId);
    if (gone === undefined || blocks.some((b) => b.id === gone.id)) {
      return;
    }
    // Not to open again if it comes back, with "Show dismissed".
    setOpenId(null);
    closedOn.current = gone.id;
    // Focus in the popup that's closing with it (a menu's button, after
    // Dismiss ▾; ADR 0194) is nowhere too.
    const focused = document.activeElement;
    if (focused !== null && focused !== document.body && focused.closest('[data-part="block-popup"]') === null) {
      return;
    }
    const next = nextCard(gone, blocks);
    (next === null ? showDismissedBox.current : cards.current.get(next))?.focus();
  }, [blocks, openId]);
  // A card dropped on another merges them, and the merged block opens (ADR
  // 0118), said through the status region (#247). Dropped on an open
  // ticket, its popup opens with its Log form on that ticket: nothing is
  // written until the tech logs it (v0.9.0's P4).
  const drag = useDragToMerge(
    blocks,
    (keptId) => {
      openBlock(keptId);
      announce("Merged into one block.");
    },
    (blockId, ticket) => {
      setLogStart({ blockId, ticketId: ticket.ticketId, number: ticket.number, closed: ticket.state === "closed" });
      openBlock(blockId);
      announce(`Dropped on ticket ${ticketName(ticket)}: nothing is written until you log it.`);
    },
  );
  // Time entered in Autotask on a ticket takes a drop too (v0.9.0's P4).
  const entryDrop = (entry: LoggedEntryView) => {
    const ticket = ticketOf(entry);
    return ticket === null ? undefined : drag.ticketDrop(`entry:${entry.id}`, ticket);
  };
  // A ticket's card takes one on its ticket, as its first block's card did
  // (ADR 0189): all its blocks are on the same ticket.
  const ticketCardDrop = (cardBlocks: readonly BlockView[]) => {
    const [first] = cardBlocks;
    return first === undefined ? undefined : drag.cardDrag(first);
  };

  // The Log form's Ticket field (ADR 0194): the tech's tickets ("logged by
  // you", a ticket's status) and the time logged to each on the day shown.
  const trayTickets = useTrayTickets(userId).data?.tickets;
  const facts = useMemo(
    () => ({
      now,
      techName: day.user.display_name,
      tickets: trayTickets,
      loggedToday: (ticketId: number) =>
        all.filter((b) => b.kind === "logged" && b.ticket?.ticketId === ticketId).reduce((sum, b) => sum + b.seconds, 0) +
        entries.filter((e) => e.ticket?.ticketId === ticketId).reduce((sum, e) => sum + e.seconds, 0),
    }),
    [now, day.user.display_name, trayTickets, all, entries],
  );
  // How the arrow keys move between the cards (ADR 0194, decision 4).
  const keysHintId = useId();
  // A card's menu, and selecting several (ADR 0194, decisions 3 and 5).
  const boardElement = useRef<HTMLDivElement>(null);
  const stillToLog = useMemo(() => all.filter((b) => b.canDismiss), [all]);
  const boardActions = useBoardActions({
    cards: onBoard,
    blocks,
    targets: stillToLog,
    timeZone,
    popupOpen: open !== null,
    openBlock,
    announce,
    techName: day.user.display_name,
    tickets: trayTickets,
    board: boardElement,
  });

  return (
    <DayFactsContext.Provider value={facts}>
    <AnnounceContext.Provider value={announce}>
      {/* There from the start, so a screen reader says each change. Hidden:
          the board already shows what happened. With the live parts, so
          what's done in a block's popup is said too (newLiveHost). */}
      {createPortal(
        <p role="status" aria-label="Block updates" className="sr-only">
          {said.message === "" ? null : <span key={said.key}>{said.message}</span>}
        </p>,
        liveHost,
      )}
      {refreshFailed ? (
        <p role="status" className="rounded-row border border-line bg-panel px-4 py-2.5 text-[13px] text-muted">
          Couldn't refresh this day. Trying again every 30 seconds.
        </p>
      ) : null}
      <StatRow day={day} isToday={isToday} userId={userId} now={now} onShowTickets={openTray} />
      {/* Every block, hidden or not: a day whose only blocks are dismissed
          still shows "Show dismissed", so they can be restored (ADR 0058). */}
      {all.length === 0 && ribbon.activities.length === 0 && entries.length === 0 ? (
        <Panel className="flex min-h-80 flex-col items-center justify-center text-center">
          <p className="text-xl font-semibold tracking-tight">
            {isToday ? "No activity yet today" : "No activity on this day"}
          </p>
          <p className="mt-1 text-muted">
            Calls, remote sessions, RMM actions and email will appear here as they are captured.
          </p>
          {userId === null ? (
            // A new tech's first day looks merely quiet until they're set up
            // (idea #869): what reaches their day is matched to them by these.
            <p className="mt-3 max-w-prose text-[13.5px] text-muted">
              {"Nothing showing up? Your calls, sessions and email reach your day once your extension, ScreenConnect name and mail are set on "}
              <Link to="/my-settings" className="font-semibold text-brand-ink underline underline-offset-2">
                My settings
              </Link>
              .
            </p>
          ) : null}
        </Panel>
      ) : (
        <>
          <RibbonTimeline
            day={day.day}
            timeZone={timeZone}
            blocks={blocks}
            activities={ribbon.activities}
            faded={ribbon.faded}
            selectedId={selectedId}
            onSelect={openBlock}
            onMenu={boardActions.onCapsuleMenu}
            now={isToday ? now : null}
          />
          {/* Under the ribbon: Show dismissed. The open tickets' tab, always
              there, replaced the pill beside it (ADR 0190). */}
          <div className="flex flex-wrap items-center gap-3 empty:hidden">
            {dismissedCount > 0 ? (
              <label className="ml-auto flex items-center gap-2 text-[13px] text-muted">
                <input
                  ref={showDismissedBox}
                  type="checkbox"
                  checked={showDismissed}
                  onChange={(event) => {
                    setShowDismissed(event.target.checked);
                  }}
                  className="size-4 accent-brand"
                />
                {`Show dismissed (${String(dismissedCount)})`}
              </label>
            ) : null}
          </div>
          {drag.error ? (
            <p role="alert" className="rounded-row border border-line bg-panel-2 px-4 py-2.5 text-[13px] text-status-needs-client-fg">
              {drag.error}
            </p>
          ) : null}
          {/* The full width of the page (ADR 0184). On a wide screen this row
              fills at least the rest of the window (the mockup's layout). Its
              columns grow with their cards, and the page scrolls (#417). Its
              description says how the arrow keys move between its cards
              (ADR 0194, decision 4; boardKeys). */}
          <span id={keysHintId} hidden>
            {BOARD_KEYS_HINT}
          </span>
          {/* A click on its empty space clears the selection (ADR 0194, decision 5). */}
          <div
            ref={boardElement}
            role="group"
            aria-label="Cards"
            aria-describedby={keysHintId}
            onClick={boardActions.onBoardClick}
            className="flex min-w-0 flex-col gap-3 min-[1100px]:min-h-[360px] min-[1100px]:flex-auto min-[1100px]:flex-row"
          >
            {COLUMNS.map((column) => {
              const cards = onBoard.filter((card) => cardColumn(card) === column.id);
              return (
                <StatusColumn
                  key={column.id}
                  columnId={column.id}
                  title={column.title}
                  count={cards.length}
                  empty={column.empty}
                >
                  {cards.map((card) => {
                    switch (card.kind) {
                      case "block":
                        return (
                          <BlockCard
                            key={card.block.id}
                            ref={cardRef([card.block.id])}
                            block={card.block}
                            timeZone={timeZone}
                            selected={card.block.id === selectedId}
                            onSelect={openBlock}
                            justClosed={justClosed.has(card.block.id)}
                            now={now}
                            drag={drag.cardDrag(card.block)}
                            board={boardActions.boardFor(card)}
                          />
                        );
                      case "entry":
                        return (
                          <LoggedEntryCard
                            key={card.entry.id}
                            entry={card.entry}
                            timeZone={timeZone}
                            now={now}
                            justClosed={justClosed.has(card.entry.id)}
                            drop={entryDrop(card.entry)}
                          />
                        );
                      case "ticket":
                        return (
                          <TicketCard
                            key={card.ticket.key}
                            ref={cardRef(card.ticket.blocks.map((b) => b.id))}
                            card={card.ticket}
                            timeZone={timeZone}
                            selected={card.ticket.blocks.some((b) => b.id === selectedId)}
                            onSelect={openBlock}
                            // Its ticket closed: every line moved to Closed together.
                            justClosed={card.ticket.lines.some((line) => justClosed.has(line.id))}
                            now={now}
                            drop={ticketCardDrop(card.ticket.blocks)}
                            board={boardActions.boardFor(card)}
                          />
                        );
                    }
                  })}
                </StatusColumn>
              );
            })}
          </div>
          {/* Room under the board for the selection bar, so it never covers the last cards. */}
          {boardActions.barShown ? <div aria-hidden="true" data-part="selection-room" className="h-20 shrink-0" /> : null}
        </>
      )}
      {boardActions.overlays}
      <BlockPopup
        block={open}
        order={order}
        timeZone={timeZone}
        targets={all.filter((b) => b.canDismiss && b.id !== open?.id)}
        onStep={(by) => {
          const next = order[order.findIndex((b) => b.id === openId) + by];
          if (next !== undefined) {
            showBlock(next.id);
          }
        }}
        // A merge that removes this block shows the kept one (#248), and its
        // heading takes focus, as stepping to it does (Idea #287).
        onSelect={(id) => {
          showBlock(id);
        }}
        // "Log a Ready block here": that block, its Log form on this ticket.
        onOpenBlock={showBlock}
        start={popupStart}
        onClose={closePopup}
        onClosed={focusAfterClosing}
        // Logged from the popup: it closes, so the card is seen landing in
        // Logged (the owner, 2026-10-21; ADR 0184), and it's said.
        onLogged={(ticket) => {
          announce(ticket === null ? "Logged to Autotask." : `Logged to Autotask: ticket ${ticket}.`);
          closePopup();
        }}
        // Dismissed from its Dismiss ▾: the board says so, with Undo (Discussion #877).
        onDismissed={(block, reason) => {
          boardActions.dismissed([block], reason);
        }}
        adoptLiveParts={adoptLiveParts}
        logStart={logStart}
        ticketCard={openTicketCard}
      />
      {/* The open-tickets drawer, with its tab always there (ADR 0190). */}
      <OpenTicketsDrawer
        open={trayOpen}
        onOpen={openTray}
        userId={userId}
        otherName={userId === null ? null : day.user.display_name}
        day={day.day}
        timeZone={timeZone}
        now={now}
        blocks={blocks}
        entries={entries}
        onOpenBlock={(id) => {
          openBlock(id);
        }}
        dropOn={drag.ticketDrop}
        onClose={closeTray}
        autoFocus={trayFocus}
      />
    </AnnounceContext.Provider>
    </DayFactsContext.Provider>
  );
}

/** Placeholders in the shapes of the real panels (section 11.3). */
function DaySkeleton() {
  const tile = "min-h-[120px] rounded-panel border border-line bg-panel motion-safe:animate-pulse";
  return (
    <div role="status" aria-label="Loading the day" className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 min-[1100px]:h-[158px] min-[1100px]:flex-row">
        <div className="min-h-[120px] rounded-panel bg-brand/40 motion-safe:animate-pulse min-[1100px]:w-[420px]" />
        <div className={`${tile} flex-1`} />
        <div className={`${tile} flex-1`} />
        <div className={`${tile} flex-1`} />
        <div className={`${tile} flex-1`} />
      </div>
      <div className="h-[168px] rounded-panel border border-line bg-panel motion-safe:animate-pulse" />
      <div className="flex flex-col gap-3 min-[1100px]:flex-row">
        {COLUMNS.map((column) => (
          <div key={column.id} className="h-64 flex-1 rounded-panel border border-line bg-panel-2 motion-safe:animate-pulse" />
        ))}
      </div>
    </div>
  );
}

/**
 * The day view's live parts: the call corner (Idea #86; AC-3) and the status
 * region that says what the tech's last action did (#247). While a block's
 * popup is open they move into it (ADR 0184), since everything else is hidden
 * then: the calls stay above its backdrop, inside its focus, and still answer
 * a click, and what's done in the popup is still said. They move back when it
 * closes. Only this element moves, never what's in it, so a call's card keeps
 * what the tech chose or typed.
 */
function newLiveHost(): HTMLElement {
  const element = document.createElement("div");
  // No box of its own, wherever it is: what's in it is fixed, or hidden.
  element.style.display = "contents";
  return element;
}

/** "Couldn't load this day", and a way to try again. */
function DayNotLoaded({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <Panel role="alert" className="flex min-h-80 flex-col items-center justify-center gap-3 text-center">
      <p className="text-xl font-semibold tracking-tight">Couldn't load this day</p>
      <p className="text-muted">{errorMessage(error)}</p>
      <button
        type="button"
        onClick={onRetry}
        className="h-10 cursor-pointer rounded-pill border border-line px-4 text-sm font-medium hover:bg-panel-2"
      >
        Try again
      </button>
    </Panel>
  );
}

export function DayPage() {
  const { day, zoneError, retryZone } = useSelectedDay();
  // An owner viewing a tech's day: whose today it is waits for the user list
  // (#862).
  if (day !== undefined) {
    return <DayOf day={day} />;
  }
  if (zoneError !== null) {
    return <DayNotLoaded error={zoneError} onRetry={retryZone} />;
  }
  return <DaySkeleton />;
}

function DayOf({ day }: { day: string }) {
  const { api } = useSession();
  const userId = useViewedUserId();
  const queryClient = useQueryClient();
  const [liveHost] = useState(newLiveHost);
  // Where the live parts are while no popup is open: at the foot of the page.
  const liveHome = useRef<HTMLDivElement | null>(null);
  const homeLiveParts = useCallback(
    (element: HTMLDivElement | null) => {
      liveHome.current = element;
      element?.appendChild(liveHost);
    },
    [liveHost],
  );
  const adoptLiveParts = useCallback<AdoptLiveParts>(
    (into) => {
      (into ?? liveHome.current)?.appendChild(liveHost);
    },
    [liveHost],
  );
  const query = useQuery({
    queryKey: ["day", day, userId ?? "me"],
    queryFn: () => {
      // Alongside the poll, not on a timer of its own (ADR 0117).
      checkForNewVersion(queryClient);
      return api.getDay(day, userId);
    },
    refetchInterval: POLL_MS,
    retry,
  });

  if (query.data !== undefined) {
    return (
      <>
        <DayView
          key={`${day}:${userId ?? "me"}`}
          day={query.data}
          userId={userId ?? null}
          refreshFailed={query.isError}
          liveHost={liveHost}
          adoptLiveParts={adoptLiveParts}
        />
        {/* The live parts: here, or in a block's popup while one is open
            (newLiveHost). */}
        <div ref={homeLiveParts} className="contents" />
        {/* The signed-in tech's own calls, whichever day is shown: ringing
          (Idea #86), above the one in progress (AC-3). */}
        {createPortal(
          <div
            data-part="call-corner"
            className="fixed right-4 bottom-4 z-40 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-3"
          >
            <RingingCalls />
            <ActiveCallPopup />
          </div>,
          liveHost,
        )}
      </>
    );
  }
  if (query.isError) {
    return (
      <DayNotLoaded
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return <DaySkeleton />;
}
